import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { router } from './router'
import { I18nProvider } from '@/i18n/I18nProvider'
import { ThemeProvider } from '@/providers/ThemeProvider'
import { AuthProvider } from '@/auth/AuthContext'
import { ToastProvider } from '@/providers/ToastProvider'
import { config } from '@/lib/config'
import './styles/globals.css'

// Stamp the brand on <html> BEFORE rendering — globals.css uses
// `:root[data-theme='billeteraPais']` / `[data-theme='totalPay']` selectors
// to override accent colours, gradients and surface tints per tenant.
// Setting it here (synchronously, before createRoot) avoids any flash of the
// wrong brand palette on the first paint.
document.documentElement.setAttribute('data-theme', config.theme)

// Update the PWA theme-color meta tag so the browser chrome (Android address
// bar, task switcher) reflects the tenant accent colour.
const THEME_COLORS: Record<string, string> = {
  billeteraPais: '#666AF2',
  masPay:        '#2E595A',
  totalPay:      '#2E595A',
}
const metaThemeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
if (metaThemeColor) metaThemeColor.content = THEME_COLORS[config.theme] ?? '#1F2937'

// Brand the browser tab title from the env-driven company label
// (VITE_COMPANY_NAME), so each white-label build shows its own name instead
// of the generic "PSP". The favicon is served statically from
// /public/icon-192x192.png (wired in index.html).
document.title = config.companyLabel || 'PSP'

// Setea el favicon dinámicamente usando VITE_ASSETS_BASE_URL para que cambie
// al cambiar de tenant, y agrega ?v=<theme> como cache-buster para que el
// browser no muestre el favicon cacheado de un tenant anterior.
const assetsBase = (import.meta.env.VITE_ASSETS_BASE_URL as string | undefined)
  || 'https://devops-stage.billeterapais.com/public'
let favicon = document.querySelector<HTMLLinkElement>("link[rel='icon']")
if (!favicon) {
  favicon = document.createElement('link')
  favicon.rel = 'icon'
  document.head.appendChild(favicon)
}
favicon.type = 'image/png'
favicon.href = `${assetsBase}/icon-192x192.png?v=${config.theme}`

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nProvider>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <Suspense fallback={null}>
              <RouterProvider router={router} />
            </Suspense>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
  </StrictMode>,
)
